import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import { useAuth } from '../../../contexts/AuthContext';
import { uploadEventImage, validateImageFile, deleteEventImage } from '../../../services/storageService';

const EventDetailsForm = ({ formData, onFormChange, onNext }) => {
  const { userProfile } = useAuth();
  const [imagePreview, setImagePreview] = useState(formData?.image || null);
  const [description, setDescription] = useState(formData?.description || '');
  const [imageUploading, setImageUploading] = useState(false);
  const [imageError, setImageError] = useState(null);

  const categoryOptions = [
    { value: 'conference', label: 'Conference' },
    { value: 'workshop', label: 'Workshop' },
    { value: 'networking', label: 'Networking' },
    { value: 'seminar', label: 'Seminar' },
    { value: 'webinar', label: 'Webinar' },
    { value: 'meetup', label: 'Meetup' },
    { value: 'exhibition', label: 'Exhibition' },
    { value: 'festival', label: 'Festival' },
    { value: 'concert', label: 'Concert' },
    { value: 'sports', label: 'Sports' },
    { value: 'other', label: 'Other' }
  ];

  const handleImageUpload = async (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    setImageError(null);
    setImageUploading(true);

    try {
      // Validate file first
      const validation = validateImageFile(file);
      if (!validation?.isValid) {
        throw new Error(validation?.error);
      }

      // Create preview immediately for better UX
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e?.target?.result);
      };
      reader?.readAsDataURL(file);

      // Generate a temporary event ID if not available
      const eventId = formData?.id || `temp-${Date.now()}`;

      // Upload to Supabase storage
      const uploadResult = await uploadEventImage(file, eventId, userProfile?.id);

      if (!uploadResult?.success) {
        throw new Error(uploadResult?.error || 'Failed to upload image');
      }

      // Update form data with storage URL and path
      onFormChange({ 
        image: uploadResult?.url,
        imagePath: uploadResult?.path,
        imageFile: file
      });

    } catch (error) {
      console.error('Error uploading image:', error);
      setImageError(error?.message || 'Failed to upload image');
      setImagePreview(null);
      onFormChange({ image: null, imagePath: null, imageFile: null });
    } finally {
      setImageUploading(false);
    }
  };

  const handleRemoveImage = async () => {
    try {
      // Delete from storage if we have a path
      if (formData?.imagePath) {
        await deleteEventImage(formData?.imagePath);
      }
      
      setImagePreview(null);
      setImageError(null);
      onFormChange({ 
        image: null, 
        imagePath: null, 
        imageFile: null 
      });
    } catch (error) {
      console.error('Error removing image:', error);
      // Still allow removal from UI even if storage deletion fails
      setImagePreview(null);
      setImageError(null);
      onFormChange({ 
        image: null, 
        imagePath: null, 
        imageFile: null 
      });
    }
  };

  const handleDescriptionChange = (e) => {
    setDescription(e?.target?.value);
    onFormChange({ description: e?.target?.value });
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    onNext();
  };

  return (
    <div className="bg-white border border-border rounded-lg p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-foreground mb-2">Event Details</h2>
        <p className="text-muted-foreground">Provide basic information about your event</p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-6">
            <Input
              label="Event Title"
              type="text"
              placeholder="Enter event title"
              value={formData?.title || ''}
              onChange={(e) => onFormChange({ title: e?.target?.value })}
              required
              description="Choose a clear, descriptive title for your event"
            />

            <Select
              label="Event Category"
              placeholder="Select category"
              options={categoryOptions}
              value={formData?.category || ''}
              onChange={(value) => onFormChange({ category: value })}
              required
              description="Help attendees find your event"
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="Start Date"
                type="date"
                value={formData?.startDate || ''}
                onChange={(e) => onFormChange({ startDate: e?.target?.value })}
                required
              />
              <Input
                label="Start Time"
                type="time"
                value={formData?.startTime || ''}
                onChange={(e) => onFormChange({ startTime: e?.target?.value })}
                required
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="End Date"
                type="date"
                value={formData?.endDate || ''}
                onChange={(e) => onFormChange({ endDate: e?.target?.value })}
                required
              />
              <Input
                label="End Time"
                type="time"
                value={formData?.endTime || ''}
                onChange={(e) => onFormChange({ endTime: e?.target?.value })}
                required
              />
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Event Image
              </label>
              <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                {imagePreview ? (
                  <div className="relative">
                    <Image
                      src={imagePreview}
                      alt="Event preview"
                      className="w-full h-48 object-cover rounded-lg"
                    />
                    {imageUploading && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-lg">
                        <div className="text-white text-center">
                          <Icon name="Loader2" size={24} className="animate-spin mx-auto mb-2" />
                          <p className="text-sm">Uploading...</p>
                        </div>
                      </div>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute top-2 right-2 bg-white shadow-md"
                      onClick={handleRemoveImage}
                      disabled={imageUploading}
                    >
                      <Icon name="X" size={16} />
                    </Button>
                  </div>
                ) : (
                  <div>
                    <Icon 
                      name={imageUploading ? "Loader2" : "Upload"} 
                      size={48} 
                      className={`mx-auto text-muted-foreground mb-4 ${imageUploading ? 'animate-spin' : ''}`} 
                    />
                    <p className="text-muted-foreground mb-2">
                      {imageUploading ? 'Uploading image...' : 'Upload event image'}
                    </p>
                    <p className="text-xs text-muted-foreground mb-4">
                      Recommended: 1200x630px, JPG/PNG/WebP, Max 10MB
                    </p>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      id="image-upload"
                      disabled={imageUploading}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => document.getElementById('image-upload')?.click()}
                      disabled={imageUploading}
                    >
                      {imageUploading ? 'Uploading...' : 'Choose File'}
                    </Button>
                  </div>
                )}
              </div>
              {imageError && (
                <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-600 flex items-center">
                    <Icon name="AlertCircle" size={16} className="mr-2" />
                    {imageError}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Event Description
          </label>
          <textarea
            value={description}
            onChange={handleDescriptionChange}
            placeholder="Describe your event in detail. What will attendees experience? What should they expect?"
            rows={6}
            className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
            required
          />
          <p className="text-xs text-muted-foreground mt-1">
            Provide a detailed description to help attendees understand your event
          </p>
        </div>

        <div className="flex justify-end pt-6 border-t border-border">
          <Button 
            type="submit" 
            iconName="ArrowRight" 
            iconPosition="right"
            disabled={imageUploading}
          >
            {imageUploading ? 'Please wait...' : 'Continue to Venue'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EventDetailsForm;